import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    InputAdornment,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "./config";
import { motion } from "framer-motion";
import { Business, Category, CheckCircle } from "@mui/icons-material";
import { AdminPageShell, AdminStat, AdminSurface } from "./admin/AdminPageShell";

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '18px',
        bgcolor: '#fff',
    },
};

const ApprovePayment = () => {
    const [businesses, setBusinesses] = useState([]);
    const [packages, setPackages] = useState([]);
    const [formData, setFormData] = useState({
        business: "",
        package: "",
    });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(API_URL + "approve.php", { withCredentials: true });
                if (response.data.success) {
                    setBusinesses(response.data.businesses);
                    setPackages(response.data.packages);
                } else {
                    setErrorMessage(response.data.message || "Failed to fetch data.");
                }
            } catch (error) {
                setErrorMessage("Error fetching data. Please try again.");
            }
        };

        fetchData();
    }, []);

    const selectedBusiness = useMemo(
        () => businesses.find((business) => business.id === formData.business),
        [businesses, formData.business],
    );
    const selectedPackage = useMemo(
        () => packages.find((pkg) => pkg.id === formData.package),
        [packages, formData.package],
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        try {
            const response = await axios.post(API_URL + "approve.php", formData, {
                withCredentials: true,
            });

            if (response.data.success) {
                setSuccessMessage(response.data.message);
                setFormData({ business: "", package: "" });
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error) {
            setErrorMessage("Error approving payment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminPageShell
            icon={<CheckCircle />}
            eyebrow="Business approvals"
            title="Approve Business"
            description="Review a business and assign the package that should become active once the payment is verified."
            maxWidth="lg"
        >
            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(240px, 320px) minmax(0, 1fr)" },
                    alignItems: "start",
                    width: "100%",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={2}>
                        <AdminStat label="Pending businesses" value={businesses.length} helper="Eligible businesses loaded from the approval queue." />
                        <AdminStat label="Available packages" value={packages.length} helper="Assign the right plan during approval." />
                    </Stack>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <AdminSurface>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                                    Approval details
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Choose the business and package, then confirm the approval.
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Select business"
                                            name="business"
                                            value={formData.business}
                                            onChange={handleInputChange}
                                            select
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Business color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        >
                                            {businesses.map((business) => (
                                                <MenuItem key={business.id} value={business.id} sx={{ py: 1.5 }}>
                                                    {business.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Select package"
                                            name="package"
                                            value={formData.package}
                                            onChange={handleInputChange}
                                            select
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Category color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        >
                                            {packages.map((pkg) => (
                                                <MenuItem key={pkg.id} value={pkg.id} sx={{ py: 1.5 }}>
                                                    {pkg.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                {(selectedBusiness || selectedPackage) && (
                                    <AdminSurface
                                        sx={{
                                            mt: 2.5,
                                            p: 2.5,
                                            background: 'linear-gradient(180deg, rgba(243,244,255,0.9) 0%, rgba(255,255,255,0.96) 100%)',
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                            Approval summary
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Typography variant="body2"><strong>Business:</strong> {selectedBusiness?.name || 'Not selected yet'}</Typography>
                                            <Typography variant="body2"><strong>Package:</strong> {selectedPackage?.name || 'Not selected yet'}</Typography>
                                        </Stack>
                                    </AdminSurface>
                                )}

                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        disabled={loading}
                                        sx={{
                                            mt: 3,
                                            py: 1.6,
                                            borderRadius: '18px',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            background:
                                                'linear-gradient(135deg, #4a90e2 0%, #6a5af9 55%, #9013fe 100%)',
                                            boxShadow: '0 18px 30px rgba(106, 90, 249, 0.24)',
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Approve Business'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </Stack>
                    </AdminSurface>
                </Box>
            </Box>

            <Snackbar open={Boolean(successMessage)} autoHideDuration={4000} onClose={() => setSuccessMessage("") }>
                <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar open={Boolean(errorMessage)} autoHideDuration={4000} onClose={() => setErrorMessage("") }>
                <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </AdminPageShell>
    );
};

export default ApprovePayment;
